import React, { useEffect } from "react";
import { useEditMyCertificationsMutation, useGetMyCertificationsQuery } from "../../store/certification/CertificationSlice";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, Grid2, FormControlLabel, Radio, Switch } from "@mui/material";
import CustomButton from "../../theme/style-components/CustomButton";
import CustomInput from "../../theme/style-components/CustomInput";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";

const validationSchema = yup.object({
  assigned_id: yup.number().required("Assigned ID is required"),
  mark_completed: yup.boolean().required("Completion status is required"),
  progress_percentage: yup
    .number()
    .required("Progress percentage is required")
    .min(0, "Cannot be less than 0")
    .max(100, "Cannot exceed 100"),
});

const EditMyCertificate = () => {

  const { id } = useParams();
  const navigate = useNavigate();

  const {
    isLoading,
    data: certificationData,
    error,
    refetch: refetchCertification,
  } = useGetMyCertificationsQuery({ assigned_id: id });

  const [editCertification] = useEditMyCertificationsMutation();

  const formik = useFormik({
    initialValues: {
      assigned_id: "",
      proof_of_certification: "",
      mark_completed: "",
      progress_percentage: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append("assigned_id", id);
        formData.append("mark_completed", values.mark_completed);
        formData.append("progress_percentage", values.progress_percentage);
        if (values.proof_of_certification instanceof File) {
          formData.append("proof_of_certification", values.proof_of_certification);
        }
        await editCertification(formData).unwrap();
        toast.success("Certificate updated successfully");
        navigate("/certifications")
      } catch (error) {
        toast.error("Failed to update certificate");
      }
    },
  });

  useEffect(() => {
    if (certificationData?.data?.length > 0) {
      const cert = certificationData.data[0];
      
      formik.setValues({
        assigned_id: cert.id || "",
        proof_of_certification: `https://api.testnet.org.inkitsolutions.com.au${cert.proof_of_certification}` || null,
        mark_completed: cert.mark_completed || false,
        progress_percentage: cert.progress_percentage || 0,
      });
    }
  }, [certificationData]);
  
  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      const allowedFormats = ["application/pdf", "image/png", "image/jpg", "image/jpeg"];
      if (!allowedFormats.includes(file.type)) {
        toast.error("Invalid file format. Only PDF, PNG, JPG, and JPEG are allowed.");
        return;
      }
      formik.setFieldValue("proof_of_certification", file);
    }
  };

  if (isLoading) return <Typography>Loading...</Typography>;

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: "15px" }}>
              Proof of Certification
            </Typography>
            <CustomInput
              type="file"
              name="proof_of_certification"
              onChange={handleFileChange}
              error={formik.touched.proof_of_certification && formik.errors.proof_of_certification}
              helperText={formik.touched.proof_of_certification && formik.errors.proof_of_certification}
              sx={{ maxWidth: 50 }}
            />
            {formik.values.proof_of_certification !== null && (
              <Typography variant="body2">
                Uploaded File: <a href={formik.values.proof_of_certification} target="_blank" rel="noopener noreferrer">View File</a>
              </Typography>
            )}
          </Box>
        </Grid2>
      </Grid2>
      
      <Grid2 container spacing={2} sx={{ mt: 2 }}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: "15px" }}>
              Progress Percentage
            </Typography>
            <CustomInput
              type="number"
              name="progress_percentage"
              value={formik.values.progress_percentage}
              onChange={formik.handleChange}
              error={formik.touched.progress_percentage && Boolean(formik.errors.progress_percentage)}
              helperText={formik.touched.progress_percentage && formik.errors.progress_percentage}
              sx={{ maxWidth: 50 }}
            />
          </Box>
        </Grid2>
      </Grid2>

      <Grid2 container spacing={2} sx={{ mt: 2 }}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: "15px" }}>
              Mark Completed
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formik.values.mark_completed}
                  onChange={(event) =>
                    formik.setFieldValue("mark_completed", event.target.checked)
                  }
                  name="mark_completed"
                  color="primary"
                />
              }
              label={formik.values.mark_completed ? "Yes" : "No"}
            />
          </Box>
        </Grid2>
      </Grid2>

      <Grid2 size={{ xs: 12 }} sx={{ mt: 4 }}>
        <Box display={"flex"} justifyContent={"end"} gap={1.5}>
          <CustomButton
            color="primary"
            type="button"
            buttonName="Cancel"
            onClick={() => navigate("/certifications")}
          />
          <CustomButton
            color="primary"
            type="button"
            buttonName="Submit"
            onClick={() => formik.handleSubmit()}
          />
        </Box>
      </Grid2>
    </Box>
  );
};

export default EditMyCertificate;
